import { AddCircle, EmojiEvent, Home, PickleBall, SearchPerson, Security } from '@/components/icons'
import { ROUTE_CLUBS, ROUTE_DASHBOARD, ROUTE_EVENTS, ROUTE_NEW_MATCH, ROUTE_PLAYERS } from '@/constants/Routes'
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux'
import { authSelector } from '@/pages/auth/selectors'
import { appSelector } from '@/pages/home/selectors'
import { userSelector } from '@/pages/user/selectors'
import { setVerifyEmailModalState } from '@/pages/user/userSlice'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { useMediaQuery } from 'usehooks-ts'
import GroupsIcon from '../icons/GroupsIcon'
import GoogleAd from './GoogleAd'
import { useHasDuprPlus, useHasFeature } from '@/hooks/useHasFeature'
import { Feature } from '@/config/features'
import { ROUTE_CLUB_MANAGEMENT } from '@/constants/Routes'
import { useHasClubManagementAccess } from '@/pages/club-management/hooks/useHasClubManagementAccess'

export const MainSidebar = () => {
  const { t } = useTranslation()

  const isMobile = useMediaQuery('(max-width: 767px)')

  const location = useLocation()
  const dispatch = useAppDispatch()
  const { token } = useAppSelector(authSelector)
  const { user } = useAppSelector(userSelector)
  const { isShowMenu, isGoogleAdsRollout, banner } = useAppSelector(appSelector)

  const onCheckVerifyEmail = () => {
    if (!user?.isValidEmail) dispatch(setVerifyEmailModalState(true))
  }

  const isDuprPlus = useHasDuprPlus()
  const duprBasicCanAddMatch = useHasFeature(Feature.DuprBasicCanAddNonClubMatch)
  const { hasAccess: hasClubManagement } = useHasClubManagementAccess()

  const canAddMatch = isDuprPlus || duprBasicCanAddMatch
  return (
    <div
      className={`${
        isShowMenu ? 'w-[200px] opacity-100 visible' : 'w-0 opacity-0 invisible'
      } transition-all duration-500 ease-in-out hidden lg:flex lg:flex-col fixed`}
    >
      {token && (
        <Link
          to={ROUTE_DASHBOARD}
          className={`flex p-2 rounded-lg ${
            location.pathname === ROUTE_DASHBOARD
              ? 'bg-gray-200 bg-opacity-10'
              : 'hover:bg-slate-200 hover:bg-opacity-5 cursor-pointer'
          } `}
        >
          {/* <AutoGraph
            className={`${location.pathname === ROUTE_DASHBOARD ? 'opacity-100' : 'opacity-50'} w-6 h-6 text-white`}
          /> */}
          <Home className={`${location.pathname === ROUTE_DASHBOARD ? 'opacity-100' : 'opacity-50'} text-white`} />
          <span
            className={`ml-4 text-base font-semibold text-white ${
              location.pathname === ROUTE_DASHBOARD ? 'opacity-100' : 'opacity-50'
            }`}
          >
            {t('common.home')}
          </span>
        </Link>
      )}
      {token && canAddMatch && (
        <Link
          to={user?.isValidEmail ? ROUTE_NEW_MATCH : ROUTE_DASHBOARD}
          className={`flex p-2 rounded-lg ${
            location.pathname === ROUTE_NEW_MATCH
              ? 'bg-gray-200 bg-opacity-10'
              : 'hover:bg-slate-200 hover:bg-opacity-5 cursor-pointer'
          } `}
          onClick={onCheckVerifyEmail}
        >
          <AddCircle className={`${location.pathname === ROUTE_NEW_MATCH ? 'opacity-100' : 'opacity-50'} text-white`} />
          <span
            className={`ml-4 text-base font-semibold text-white ${
              location.pathname === ROUTE_NEW_MATCH ? 'opacity-100' : 'opacity-50'
            }`}
          >
            {t('common.newMatch')}
          </span>
        </Link>
      )}
      <Link
        to={ROUTE_PLAYERS}
        className={`flex p-2 rounded-lg ${
          location.pathname === ROUTE_PLAYERS
            ? 'bg-gray-200 bg-opacity-10'
            : 'hover:bg-slate-200 hover:bg-opacity-5 cursor-pointer'
        } `}
      >
        <SearchPerson className={`${location.pathname === ROUTE_PLAYERS ? 'opacity-100' : 'opacity-50'} text-white`} />
        <span
          className={`ml-4 text-base font-semibold text-white ${
            location.pathname === ROUTE_PLAYERS ? 'opacity-100' : 'opacity-50'
          }`}
        >
          {t('common.players')}
        </span>
      </Link>

      {token && (
        <Link
          to={ROUTE_CLUBS}
          className={`flex p-2 rounded-lg ${
            location.pathname === ROUTE_CLUBS
              ? 'bg-gray-200 bg-opacity-10'
              : 'hover:bg-slate-200 hover:bg-opacity-5 cursor-pointer'
          } `}
        >
          <GroupsIcon className={`${location.pathname === ROUTE_CLUBS ? 'opacity-100' : 'opacity-50'} text-white`} />
          <span
            className={`ml-4 text-base font-semibold text-white ${
              location.pathname === ROUTE_CLUBS ? 'opacity-100' : 'opacity-50'
            }`}
          >
            {t('common.clubs')}
          </span>
        </Link>
      )}

      {token && hasClubManagement && (
        <Link
          to={ROUTE_CLUB_MANAGEMENT}
          className={`flex p-2 rounded-lg ${
            location.pathname === ROUTE_CLUB_MANAGEMENT
              ? 'bg-gray-200 bg-opacity-10'
              : 'hover:bg-slate-200 hover:bg-opacity-5 cursor-pointer'
          }`}
        >
          <GroupsIcon
            className={`${location.pathname === ROUTE_CLUB_MANAGEMENT ? 'opacity-100' : 'opacity-50'} text-white`}
          />
          <span
            className={`ml-4 text-base font-semibold text-white ${
              location.pathname === ROUTE_CLUB_MANAGEMENT ? 'opacity-100' : 'opacity-50'
            }`}
          >
            {t('club.management', 'Club Overview')}
          </span>
          <span className="ml-2 self-start mt-1 inline-flex items-center px-1.5 py-0.5 text-[9px] font-bold border border-yellow-400 text-yellow-400 bg-transparent rounded flex-shrink-0">
            BETA
          </span>
        </Link>
      )}

      {token && (
        <Link
          to={ROUTE_EVENTS}
          className={`flex p-2 rounded-lg ${
            location.pathname === ROUTE_EVENTS
              ? 'bg-gray-200 bg-opacity-10'
              : 'hover:bg-slate-200 hover:bg-opacity-5 cursor-pointer'
          } `}
        >
          <PickleBall className={`${location.pathname === ROUTE_EVENTS ? 'opacity-100' : 'opacity-50'} text-white`} />
          <span
            className={`ml-4 text-base font-semibold text-white ${
              location.pathname === ROUTE_EVENTS ? 'opacity-100' : 'opacity-50'
            }`}
          >
            {t('common.events')}
          </span>
        </Link>
      )}
      <Link
        to="https://www.dupr.com/minorleague"
        target="_blank"
        className="flex p-2 rounded-lg hover:bg-slate-200 hover:bg-opacity-5 cursor-pointer"
      >
        <div>
          <EmojiEvent className="opacity-50 text-white" />
        </div>
        <span className="ml-4 text-base font-semibold text-white opacity-50">{t('common.minorLeaguePickleball')}</span>
      </Link>
      <Link
        to="https://www.dupr.com/collegiate"
        target="_blank"
        className="flex p-2 rounded-lg hover:bg-slate-200 hover:bg-opacity-5 cursor-pointer"
      >
        <div>
          <EmojiEvent className="opacity-50 text-white" />
        </div>
        <span className="ml-4 text-base font-semibold text-white opacity-50">{t('common.collegiatePickleball')}</span>
      </Link>
      <Link
        to="https://www.dupr.com/blog"
        target="_blank"
        className="flex p-2 rounded-lg hover:bg-slate-200 hover:bg-opacity-5 cursor-pointer"
      >
        <div>
          <Security className="opacity-50 text-white" />
        </div>
        <span className="ml-4 text-base font-semibold text-white opacity-50">{t('common.blog')}</span>
      </Link>

      {isGoogleAdsRollout && !isMobile ? (
        <GoogleAd />
      ) : (
        banner !== null && (
          <Link to={banner.link} className="mt-4" target="_blank">
            <img src={banner.imageUrl} className="max-w-[200px] 2xl:max-w-[240px]" />
          </Link>
        )
      )}
    </div>
  )
}
